// src/pages/OrdersPage.jsx
import { useEffect, useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import { getOrders, updateOrder, deleteOrder, cancelOrder, markRefunded, upsertCustomerFromOrder } from '../lib/db';
import { PrintTicket } from '../components/PrintTicket';
import { PrintInvoice } from '../components/PrintInvoice';
import { PrintSuperTicket } from '../components/PrintSuperTicket';
import { InvoiceModal } from '../components/InvoiceModal';
import { printDocument } from '../lib/print';
import { cancellationOf, paymentSummary } from '../lib/invoice';
import { titleCaseName, sentenceCase } from '../lib/text';
import {
  schedule, workflowOf, neededDateOf, neededTimeOf, fmtDay, fmtTime, relativePhrase,
} from '../lib/priority';

const STATUS_OPTIONS = ['all','pending','draft','completed','paid','void','ready'];
// The stored value stays 'void' so orders cancelled before this existed still
// load, but nothing user-facing says it — the shop says "cancelled".
const STATUS_LABEL = {
  all: 'All Statuses', pending: 'Pending', draft: 'Draft', ready: 'Ready',
  completed: 'Completed', paid: 'Paid', void: 'Cancelled',
};
const TYPE_OPTIONS   = ['all','carry_out','local_delivery','pick_up','wire_out','wire_in'];
const TYPE_LABEL = { carry_out:'Carry Out', local_delivery:'Local Delivery', pick_up:'Pick Up', wire_out:'Wire Out', wire_in:'Wire In' };
const SOURCE_OPTIONS = ['all','online','pos'];
const SOURCE_LABEL = { all:'All Sources', online:'Online', pos:'In-Store' };
const fmt = n => '$' + (n||0).toFixed(2);

// Name is stored split on new orders but legacy rows only have a combined
// `customerName`, so fall back to splitting it on the first space.
function firstNameOf(o) {
  if (o.billing?.firstName) return o.billing.firstName;
  return (o.customerName || '').trim().split(/\s+/)[0] || '';
}
function lastNameOf(o) {
  if (o.billing?.lastName) return o.billing.lastName;
  return (o.customerName || '').trim().split(/\s+/).slice(1).join(' ');
}
// Whoever we'd actually ring about this order — the buyer, or the recipient
// if no buyer number was taken.
function phoneOf(o) {
  return o.billing?.phone || o.recipient?.phone || '';
}

export function OrdersPage() {
  const { shopId, shop, profile } = useAuth();
  const [orders,  setOrders]  = useState([]);
  const [loading, setLoading] = useState(true);
  const [search,  setSearch]  = useState('');
  const [statusF, setStatusF] = useState('all');
  const [typeF,   setTypeF]   = useState('all');
  const [sourceF, setSourceF] = useState('all');
  const [selected,setSelected]= useState(null);
  const [editing, setEditing] = useState(null);
  const [invoicing, setInvoicing] = useState(null);
  const [cancelling, setCancelling] = useState(null);

  useEffect(() => {
    if (!shopId) return;
    loadOrders();
  }, [shopId]);

  async function loadOrders() {
    setLoading(true);
    try {
      const data = await getOrders(shopId);
      setOrders(data);
      linkOnlineCustomers(data);
    } finally { setLoading(false); }
  }

  /**
   * Counter orders create/update their customer record on the way in
   * (NewOrderPage → upsertCustomerFromOrder). Online orders bypass that —
   * the storefront writes straight to Firestore with no auth, so it can't
   * touch the customers collection at all. Do the same linking here, once
   * per order (guarded by customerLinked), so an online order's buyer shows
   * up in the Customers tab exactly like a walk-in would.
   */
  async function linkOnlineCustomers(list) {
    const pending = list.filter(o => o.source === 'online' && !o.customerLinked);
    for (const o of pending) {
      try {
        const customerId = await upsertCustomerFromOrder(shopId, { billing: o.billing, recipient: o.recipient });
        if (customerId) {
          await updateOrder(shopId, o.id, { customerId, customerLinked: true });
          setOrders(os => os.map(x => x.id === o.id ? { ...x, customerId, customerLinked: true } : x));
        }
      } catch (err) {
        console.error('[linkOnlineCustomers]', o.id, err);
      }
    }
  }

  async function changeStatus(orderId, status) {
    await updateOrder(shopId, orderId, { status });
    setOrders(o => o.map(x => x.id === orderId ? { ...x, status } : x));
    if (selected?.id === orderId) setSelected(s => ({ ...s, status }));
  }

  /**
   * The printable documents differ in orientation/page size as well as
   * content, so all three go through printDocument() — see src/lib/print.js.
   */
  function printInvoice()     { printDocument('invoice'); }
  function printTicket()      { printDocument('ticket'); }
  function printSuperTicket() { printDocument('superticket'); }

  async function handleDelete(orderId) {
    if (!window.confirm('Delete this order? This cannot be undone.')) return;
    try {
      await deleteOrder(shopId, orderId);
      setOrders(o => o.filter(x => x.id !== orderId));
      setSelected(null);
    } catch (err) {
      console.error('[handleDelete]', orderId, err);
      window.alert('Could not delete this order: ' + err.message);
    }
  }

  const filtered = orders.filter(o => {
    if (statusF !== 'all' && o.status !== statusF) return false;
    if (typeF   !== 'all' && o.type   !== typeF)   return false;
    if (sourceF === 'online' && o.source !== 'online') return false;
    if (sourceF === 'pos'    && o.source === 'online') return false;
    if (search) {
      const t = search.toLowerCase();
      const haystack = [
        o.customerName, firstNameOf(o), lastNameOf(o),
        String(o.orderNumber || ''),
        // Digits only, so "8675550134" finds "(867) 555-0134".
        phoneOf(o), phoneOf(o).replace(/\D/g, ''),
      ].join(' ').toLowerCase();
      const digits = t.replace(/\D/g, '');
      if (!haystack.includes(t) && !(digits && haystack.includes(digits))) return false;
    }
    return true;
  });

  return (
    <div style={{display:'flex',gap:16,height:'calc(100vh - 52px - 40px)'}}>
      {/* List */}
      <div style={{flex:1,overflow:'hidden',display:'flex',flexDirection:'column'}}>
        {/* Filters */}
        <div className="card" style={{marginBottom:12,padding:'12px 16px'}}>
          <div style={{display:'flex',gap:10,flexWrap:'wrap',alignItems:'center'}}>
            <input className="input" style={{flex:1,minWidth:180}} placeholder="Search by name, phone or order #"
              value={search} onChange={e=>setSearch(e.target.value)}/>
            <select className="input" style={{width:140}} value={statusF} onChange={e=>setStatusF(e.target.value)}>
              {STATUS_OPTIONS.map(s=><option key={s} value={s}>{STATUS_LABEL[s] || capitalize(s)}</option>)}
            </select>
            <select className="input" style={{width:160}} value={typeF} onChange={e=>setTypeF(e.target.value)}>
              {TYPE_OPTIONS.map(t=><option key={t} value={t}>{t==='all'?'All Types':(TYPE_LABEL[t]||t)}</option>)}
            </select>
            <select className="input" style={{width:140}} value={sourceF} onChange={e=>setSourceF(e.target.value)}>
              {SOURCE_OPTIONS.map(s=><option key={s} value={s}>{SOURCE_LABEL[s]}</option>)}
            </select>
            <button className="btn btn-ghost btn-sm" onClick={loadOrders}>↻ Refresh</button>
          </div>
        </div>

        <div style={{flex:1,overflow:'auto'}}>
          {loading ? (
            <div className="loading-center"><div className="spinner"/></div>
          ) : filtered.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">📋</div>
              <h3>No orders found</h3>
              <p>Try adjusting your filters.</p>
            </div>
          ) : (
            <div className="card" style={{padding:0,overflow:'hidden'}}>
              <table className="data-table">
                <thead>
                  <tr>
                    <th>#</th><th>First Name</th><th>Last Name</th><th>Phone</th>
                    <th>Type</th><th>Total</th><th>Status</th><th>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(o => (
                    <tr key={o.id}
                      style={{cursor:'pointer',background: selected?.id===o.id ? 'var(--g50)' : ''}}
                      onClick={()=>setSelected(o)}>
                      <td style={{fontWeight:600,color:'var(--g700)'}}>#{o.orderNumber||'—'}</td>
                      <td>{firstNameOf(o) || '—'}</td>
                      <td style={{fontWeight:600}}>{lastNameOf(o) || '—'}</td>
                      <td style={{whiteSpace:'nowrap'}}>
                        {phoneOf(o)
                          ? <a href={`tel:${phoneOf(o)}`} onClick={e=>e.stopPropagation()}
                              style={{color:'var(--g700)',textDecoration:'none'}}>{phoneOf(o)}</a>
                          : <span style={{color:'var(--text-muted)'}}>—</span>}
                      </td>
                      <td>
                        <span className="pill pill-gray" style={{fontSize:11}}>{TYPE_LABEL[o.type]||o.type}</span>
                        {o.source === 'online' && (
                          <span className="pill pill-blue" style={{fontSize:11,marginLeft:4}}>Online</span>
                        )}
                      </td>
                      <td style={{fontWeight:600}}>{fmt(o.total)}</td>
                      <td><StatusPill status={o.status}/></td>
                      <td style={{fontSize:12,color:'var(--text-muted)'}}>{fmtDate(o.createdAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Detail panel */}
      {selected && (
        <div style={{width:320,flexShrink:0,overflow:'auto'}}>
          <div className="card">
            <div style={{display:'flex',justifyContent:'space-between',marginBottom:14}}>
              <h2>Order #{selected.orderNumber||'—'}</h2>
              <button style={{background:'none',border:'none',fontSize:18,cursor:'pointer',color:'var(--text-muted)'}} onClick={()=>setSelected(null)}>✕</button>
            </div>

            <div style={{marginBottom:14}}>
              <StatusPill status={selected.status}/>
              <span style={{marginLeft:8,fontSize:12.5,color:'var(--text-muted)'}}>{TYPE_LABEL[selected.type]||selected.type}</span>
              {selected.source === 'online' && (
                <span className="pill pill-blue" style={{fontSize:11,marginLeft:8}}>Online</span>
              )}
            </div>

            <div style={{fontSize:13,lineHeight:1.8,color:'var(--text-mid)',marginBottom:14}}>
              <div><strong>Customer:</strong> {`${firstNameOf(selected)} ${lastNameOf(selected)}`.trim() || '—'}</div>
              {phoneOf(selected) && (
                <div><strong>Phone:</strong>{' '}
                  <a href={`tel:${phoneOf(selected)}`} style={{color:'var(--g700)'}}>{phoneOf(selected)}</a>
                </div>
              )}
              <div><strong>Total:</strong> {fmt(selected.total)}</div>
              <div><strong>Subtotal:</strong> {fmt(selected.subtotal)}</div>
              {selected.deliveryFee > 0 && <div><strong>Delivery:</strong> {fmt(selected.deliveryFee)}</div>}
              <div><strong>Tax:</strong> {fmt(selected.tax)}</div>
              {selected.recipient?.firstName && (
                <div><strong>Recipient:</strong> {selected.recipient.firstName} {selected.recipient.lastName}</div>
              )}
              {selected.delivery?.date && <div><strong>Delivery Date:</strong> {selected.delivery.date}</div>}
              {selected.recipient?.occasion && selected.recipient.occasion !== 'Other' && (
                <div><strong>Occasion:</strong> {selected.recipient.occasion}</div>
              )}
            </div>

            {selected.items?.length > 0 && (
              <div style={{marginBottom:14}}>
                <div style={{fontSize:11,fontWeight:700,textTransform:'uppercase',letterSpacing:'0.7px',color:'var(--text-muted)',marginBottom:8}}>Items</div>
                {selected.items.map((item,i) => (
                  <div key={i} style={{display:'flex',justifyContent:'space-between',fontSize:13,padding:'4px 0',borderBottom:'1px solid var(--parch-mid)'}}>
                    <span>{item.qty}× {item.description||item.productId}</span>
                    <span style={{fontWeight:600}}>{fmt(item.qty*item.price)}</span>
                  </div>
                ))}
              </div>
            )}

            {selected.recipient?.cardMessage && (
              <div style={{background:'var(--parch)',border:'1px solid var(--border)',borderRadius:'var(--radius-sm)',padding:'10px 12px',fontSize:13,fontStyle:'italic',marginBottom:14,color:'var(--text-mid)'}}>
                "{selected.recipient.cardMessage}"
              </div>
            )}

            {/* Status actions */}
            <div style={{marginBottom:10}}>
              <div style={{fontSize:11,fontWeight:700,textTransform:'uppercase',letterSpacing:'0.7px',color:'var(--text-muted)',marginBottom:8}}>Change Status</div>
              <div style={{display:'flex',gap:6,flexWrap:'wrap'}}>
                {['pending','ready','completed','paid'].map(s => (
                  <button key={s}
                    className={`btn btn-sm ${selected.status===s ? 'btn-primary' : 'btn-secondary'}`}
                    onClick={()=>changeStatus(selected.id,s)}>
                    {capitalize(s)}
                  </button>
                ))}
              </div>
            </div>

            <button
              className="btn btn-primary btn-sm"
              style={{width:'100%', marginBottom:8}}
              onClick={() => setEditing(selected)}
            >
              ✏️ Edit Order
            </button>

            <button
              className="btn btn-gold btn-sm"
              style={{width:'100%', marginBottom:8}}
              onClick={() => setInvoicing(selected)}
            >
              🧾 Invoice
            </button>

            <button
              className="btn btn-secondary btn-sm"
              style={{width:'100%', marginBottom:8}}
              onClick={printTicket}
            >
              🖨 Print Ticket
            </button>

            <button
              className="btn btn-secondary btn-sm"
              style={{width:'100%', marginBottom:8}}
              onClick={printSuperTicket}
              title="Prints on legal-size (21.5 x 35.5cm) pre-printed stock — set your printer driver to route this size to the superticket tray."
            >
              🖨 Print Super Ticket
            </button>

            {cancellationOf(selected).cancelled ? (
              <CancelledPanel
                order={selected}
                onRefunded={async () => {
                  await markRefunded(shopId, selected.id, profile?.displayName);
                  await loadOrders();
                  setSelected(s => ({ ...s, cancellation: { ...(s.cancellation||{}),
                    refundedAt: new Date().toISOString(), refundedBy: profile?.displayName } }));
                }}
              />
            ) : (
              <button className="btn btn-danger btn-sm" style={{width:'100%', marginBottom:8}}
                onClick={()=>setCancelling(selected)}>
                ✕ Cancel Order
              </button>
            )}

            <button className="btn btn-danger btn-sm" style={{width:'100%'}} onClick={()=>handleDelete(selected.id)}>
              Delete Order
            </button>
          </div>
        </div>
      )}

      {editing && (
        <EditOrderModal
          order={editing}
          shop={shop}
          onClose={()=>setEditing(null)}
          onSaved={(patch)=>{
            const merged = { ...editing, ...patch };
            setOrders(os => os.map(o => o.id === editing.id ? merged : o));
            setSelected(s => s?.id === editing.id ? merged : s);
            setEditing(null);
          }}
        />
      )}

      {invoicing && (
        <InvoiceModal
          order={invoicing}
          onClose={()=>setInvoicing(null)}
          onPrint={()=>printInvoice()}
        />
      )}

      {cancelling && (
        <CancelOrderModal
          order={cancelling}
          onClose={()=>setCancelling(null)}
          onConfirm={async ({ reason, refundDue }) => {
            await cancelOrder(shopId, cancelling.id, {
              reason, refundDue,
              wasPaid: paymentSummary(cancelling).settled,
              by: profile?.displayName,
            });
            await loadOrders();
            const patch = { status:'void', cancellation:{ reason, refundDue,
              wasPaid: paymentSummary(cancelling).settled,
              by: profile?.displayName || null, at: new Date().toISOString(), refundedAt: null } };
            setSelected(s => s?.id === cancelling.id ? { ...s, ...patch } : s);
            setCancelling(null);
          }}
        />
      )}

      {/* Hidden print documents. The body class picks which one prints. */}
      <PrintTicket order={selected}/>
      <PrintInvoice order={invoicing || selected}/>
      <PrintSuperTicket order={selected}/>
    </div>
  );
}

const OCCASIONS = ['Other','Birthday','Anniversary','Sympathy','Congratulations','Get Well','Romance','Thank You','New Baby','Funeral'];

/**
 * Edit an order after it's been taken — dates move, addresses get corrected,
 * customers add a vase.
 *
 * Two things matter beyond just saving fields:
 *  - totals are recomputed from the items, because the total drives the
 *    big-order priority rules; and
 *  - the schedule is re-derived, so moving a date immediately re-slots the
 *    order on the Workboard instead of leaving a stale make-by date behind.
 */
function EditOrderModal({ order, shop, onClose, onSaved }) {
  const { shopId, profile } = useAuth();
  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState('');

  const [form, setForm] = useState(() => {
    const storedSub = order.subtotal || 0;
    const storedSvc = order.serviceFee || 0;
    // Recover the rate this order actually used — it may have been overridden
    // at the counter — rather than silently re-taxing at today's shop default.
    const derivedRate = (storedSub + storedSvc) > 0
      ? (order.tax || 0) / (storedSub + storedSvc)
      : (shop?.taxRate ?? 0.05);
    return {
      neededDate: neededDateOf(order) || '',
      neededTime: neededTimeOf(order) || '',
      outOfTown:  !!order.fulfillment?.outOfTown,
      flight:     { airline:'', number:'', date:'', departTime:'', ...(order.fulfillment?.flight || {}) },
      customer:   { firstName: firstNameOf(order), lastName: lastNameOf(order), phone: phoneOf(order) },
      recipient:  { firstName:'', lastName:'', address:'', city:'', stPr:'', phone:'',
                    cardMessage:'', occasion:'Other', ...(order.recipient || {}) },
      instructions: order.delivery?.instructions || '',
      items: (order.items || []).map(i => ({ ...i })),
      deliveryFee: order.deliveryFee || 0,
      serviceFee:  storedSvc,
      taxRate:     Math.round(derivedRate * 10000) / 10000,
    };
  });

  // Same gentle capitalisation as the order form, so editing a name doesn't
  // undo it. See src/lib/text.js.
  const NAME_FIELDS  = ['firstName', 'lastName', 'city', 'address'];
  const PROSE_FIELDS = ['cardMessage', 'instructions'];
  const tidy = (k, v) =>
    typeof v !== 'string' ? v
    : NAME_FIELDS.includes(k)  ? titleCaseName(v)
    : PROSE_FIELDS.includes(k) ? sentenceCase(v)
    : v;

  const setF = (k) => (e) => setForm(f => ({ ...f, [k]: tidy(k, e.target.value) }));
  const setSub = (grp, k) => (e) => setForm(f => ({ ...f, [grp]: { ...f[grp], [k]: tidy(k, e.target.value) } }));
  const num = (v) => parseFloat(v) || 0;

  function setItem(i, k, v) {
    setForm(f => ({ ...f, items: f.items.map((it, idx) =>
      idx === i ? { ...it, [k]: (k === 'description' ? v : num(v)) } : it) }));
  }
  const addItem    = () => setForm(f => ({ ...f, items: [...f.items, { description:'', qty:1, price:0, discount:0 }] }));
  const removeItem = (i) => setForm(f => ({ ...f, items: f.items.filter((_, idx) => idx !== i) }));

  const subtotal = form.items.reduce((s, i) => s + num(i.qty) * num(i.price) * (1 - num(i.discount) / 100), 0);
  const tax      = (subtotal + num(form.serviceFee)) * num(form.taxRate);
  const total    = subtotal + num(form.deliveryFee) + num(form.serviceFee) + tax;

  const wf = workflowOf(shop);
  const preview = schedule({
    total, recipient: form.recipient,
    fulfillment: {
      neededDate: form.neededDate, neededTime: form.neededTime,
      outOfTown: form.outOfTown, flight: form.outOfTown ? form.flight : null,
    },
  }, wf);

  const dateMoved  = (neededDateOf(order) || '') !== form.neededDate;
  const totalMoved = Math.abs((order.total || 0) - total) > 0.005;

  async function handleSave(e) {
    e.preventDefault();
    setSaving(true); setError('');
    try {
      const patch = {
        items: form.items.filter(i => i.description),
        subtotal,
        deliveryFee: num(form.deliveryFee),
        serviceFee:  num(form.serviceFee),
        tax, total,
        customerName: `${form.customer.firstName} ${form.customer.lastName}`.trim(),
        // Passed as a whole object so updateOrder's scrubBilling runs over it —
        // editing an old order also strips any card data it still carries.
        billing: { ...(order.billing || {}),
                   firstName: form.customer.firstName,
                   lastName:  form.customer.lastName,
                   phone:     form.customer.phone },
        recipient: form.recipient,
        delivery: { ...(order.delivery || {}),
                    date: form.neededDate, time: form.neededTime || 'None',
                    instructions: form.instructions },
        fulfillment: {
          ...(order.fulfillment || {}),
          neededDate: form.neededDate,
          neededTime: form.neededTime,
          outOfTown:  form.outOfTown,
          flight:     form.outOfTown ? { ...form.flight, date: form.flight.date || form.neededDate } : null,
          leadDays:   preview.leadDays,
          startBy:    preview.startBy,
          finishBy:   preview.finishBy,
        },
        ...(order.type === 'pick_up' && { pickupDate: form.neededDate, pickupTime: form.neededTime }),
        lastEditedBy: profile?.displayName || null,
      };
      await updateOrder(shopId, order.id, patch);
      onSaved(patch);
    } catch (err) {
      setError('Could not save changes: ' + err.message);
      setSaving(false);
    }
  }

  return (
    <div className="modal-overlay" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="modal" style={{maxWidth:760}}>
        <div className="modal-header">
          <h2>Edit Order #{order.orderNumber}</h2>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        {error && <div className="auth-error">{error}</div>}

        <form onSubmit={handleSave}>
          {/* ── When it's needed ── */}
          <div className="section-label">When it's needed</div>
          <div className="form-row">
            <div className="form-group flex1"><label className="field-label">Needed Date</label>
              <input className="input" type="date" value={form.neededDate} onChange={setF('neededDate')}/>
            </div>
            <div className="form-group flex1"><label className="field-label">Time</label>
              <input className="input" type="time" value={form.neededTime} onChange={setF('neededTime')}/>
            </div>
          </div>
          <label className="checkbox-label">
            <input type="checkbox" checked={form.outOfTown}
              onChange={e => setForm(f => ({ ...f, outOfTown: e.target.checked }))}/>
            {' '}Going out of town (flying)
          </label>
          {form.outOfTown && (
            <div className="form-row" style={{marginTop:8}}>
              <div className="form-group flex1"><label className="field-label">Airline</label>
                <input className="input" value={form.flight.airline} onChange={setSub('flight','airline')}/>
              </div>
              <div className="form-group w-sm"><label className="field-label">Flight #</label>
                <input className="input" value={form.flight.number} onChange={setSub('flight','number')}/>
              </div>
              <div className="form-group flex1"><label className="field-label">Flight Date</label>
                <input className="input" type="date" value={form.flight.date || form.neededDate} onChange={setSub('flight','date')}/>
              </div>
              <div className="form-group w-sm"><label className="field-label">Departs</label>
                <input className="input" type="time" value={form.flight.departTime} onChange={setSub('flight','departTime')}/>
              </div>
            </div>
          )}

          {form.neededDate ? (
            <div className={`lead-preview ${dateMoved ? 'tone-amber' : ''}`}>
              🌸 Make by <strong>{fmtDay(preview.finishBy)}</strong>
              {relativePhrase(preview.finishBy) && <> ({relativePhrase(preview.finishBy)})</>}
              {preview.startBy !== preview.finishBy && (
                <> · start <strong>{fmtDay(preview.startBy)}</strong> — {preview.reasons.map(r=>r.label).join(', ')}</>
              )}
              {preview.dropOffBy && <> · airport by <strong>{fmtTime(preview.dropOffBy)}</strong></>}
              {dateMoved && <div><strong>Date changed</strong> — the Workboard will re-slot this order on save.</div>}
            </div>
          ) : (
            <div className="lead-preview tone-red">⚠️ No date — this order won't appear on the Workboard.</div>
          )}

          {/* ── People ── */}
          <div className="section-label" style={{marginTop:16}}>Customer</div>
          <div className="form-row">
            <div className="form-group flex1"><label className="field-label">First Name</label>
              <input className="input" value={form.customer.firstName} onChange={setSub('customer','firstName')}/>
            </div>
            <div className="form-group flex1"><label className="field-label">Last Name</label>
              <input className="input" value={form.customer.lastName} onChange={setSub('customer','lastName')}/>
            </div>
            <div className="form-group flex1"><label className="field-label">Phone</label>
              <input className="input" value={form.customer.phone} onChange={setSub('customer','phone')}/>
            </div>
          </div>

          <div className="section-label" style={{marginTop:10}}>Recipient</div>
          <div className="form-row">
            <div className="form-group flex1"><label className="field-label">First Name</label>
              <input className="input" value={form.recipient.firstName} onChange={setSub('recipient','firstName')}/>
            </div>
            <div className="form-group flex1"><label className="field-label">Last Name</label>
              <input className="input" value={form.recipient.lastName} onChange={setSub('recipient','lastName')}/>
            </div>
            <div className="form-group flex1"><label className="field-label">Phone</label>
              <input className="input" value={form.recipient.phone} onChange={setSub('recipient','phone')}/>
            </div>
          </div>
          <div className="form-row">
            <div className="form-group flex1"><label className="field-label">Address</label>
              <input className="input" value={form.recipient.address} onChange={setSub('recipient','address')}/>
            </div>
            <div className="form-group flex1"><label className="field-label">City</label>
              <input className="input" value={form.recipient.city} onChange={setSub('recipient','city')}/>
            </div>
            <div className="form-group w-sm"><label className="field-label">St/Pr</label>
              <input className="input" value={form.recipient.stPr} onChange={setSub('recipient','stPr')}/>
            </div>
          </div>
          <div className="form-row">
            <div className="form-group flex1"><label className="field-label">Card Message</label>
              <textarea className="input" rows={2} value={form.recipient.cardMessage} onChange={setSub('recipient','cardMessage')}/>
            </div>
            <div className="form-group flex1"><label className="field-label">Occasion</label>
              <select className="input" value={form.recipient.occasion} onChange={setSub('recipient','occasion')}>
                {OCCASIONS.map(o => <option key={o}>{o}</option>)}
              </select>
            </div>
          </div>
          <div className="form-group"><label className="field-label">Instructions</label>
            <textarea className="input" rows={2} value={form.instructions} onChange={setF('instructions')}/>
          </div>

          {/* ── Items ── */}
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginTop:14}}>
            <div className="section-label" style={{marginBottom:0}}>Items</div>
            <button type="button" className="btn btn-ghost btn-sm" onClick={addItem}>+ Add Item</button>
          </div>
          <div className="table-wrap">
            <table className="data-table">
              <thead><tr><th></th><th>Description</th><th>Qty</th><th>Price</th><th>Disc%</th><th>Ext.</th></tr></thead>
              <tbody>
                {form.items.map((it, i) => (
                  <tr key={i}>
                    <td><button type="button" className="btn btn-danger btn-sm btn-icon-only" onClick={()=>removeItem(i)}>✕</button></td>
                    <td><input className="input" value={it.description||''} onChange={e=>setItem(i,'description',e.target.value)}/></td>
                    <td><input className="input sm" type="number" min="1" value={it.qty??1} onChange={e=>setItem(i,'qty',e.target.value)}/></td>
                    <td><input className="input sm" type="number" min="0" step="0.01" value={it.price??0} onChange={e=>setItem(i,'price',e.target.value)}/></td>
                    <td><input className="input sm" type="number" min="0" max="100" value={it.discount??0} onChange={e=>setItem(i,'discount',e.target.value)}/></td>
                    <td style={{fontWeight:600,color:'var(--g700)'}}>
                      {fmt(num(it.qty) * num(it.price) * (1 - num(it.discount)/100))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* ── Money ── */}
          <div className="form-row" style={{marginTop:12}}>
            <div className="form-group flex1"><label className="field-label">Delivery Fee</label>
              <input className="input" type="number" step="0.01" min="0" value={form.deliveryFee} onChange={setF('deliveryFee')}/>
            </div>
            <div className="form-group flex1"><label className="field-label">Service Fee</label>
              <input className="input" type="number" step="0.01" min="0" value={form.serviceFee} onChange={setF('serviceFee')}/>
            </div>
            <div className="form-group flex1"><label className="field-label">Tax Rate (%)</label>
              <input className="input" type="number" step="0.01" min="0" max="30"
                value={Math.round(num(form.taxRate) * 10000) / 100}
                onChange={e => setForm(f => ({ ...f, taxRate: num(e.target.value) / 100 }))}/>
            </div>
          </div>

          <div style={{background:'var(--parch)',borderRadius:'var(--radius-sm)',padding:'10px 12px',marginTop:4}}>
            <div className="summary-row"><span>Subtotal</span><span>{fmt(subtotal)}</span></div>
            <div className="summary-row"><span>Tax</span><span>{fmt(tax)}</span></div>
            <div className="summary-row total">
              <span>Total</span>
              <span>
                {totalMoved && (
                  <span style={{color:'var(--text-muted)',fontWeight:400,marginRight:8,textDecoration:'line-through'}}>
                    {fmt(order.total)}
                  </span>
                )}
                {fmt(total)}
              </span>
            </div>
          </div>

          <div style={{display:'flex',gap:8,justifyContent:'flex-end',marginTop:16}}>
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Saving…' : 'Save changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

const CANCEL_REASONS = [
  'Customer changed their mind',
  'Duplicate order',
  'Out of stock / cannot make it',
  'Entered by mistake',
  'Other',
];

/**
 * Cancelling used to be a single click in the status row, with no record of
 * why and — more importantly — no note that a paid customer is owed money back.
 */
function CancelOrderModal({ order, onClose, onConfirm }) {
  const pay = paymentSummary(order);
  const [reason, setReason]       = useState(CANCEL_REASONS[0]);
  const [other,  setOther]        = useState('');
  const [refund, setRefund]       = useState(pay.settled);   // paid ⇒ money goes back
  const [saving, setSaving]       = useState(false);

  const finalReason = reason === 'Other' ? other.trim() : reason;

  return (
    <div className="modal-overlay" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="modal" style={{ maxWidth: 480 }}>
        <div className="modal-header">
          <h2>Cancel order #{order.orderNumber}</h2>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>

        <p style={{ fontSize:13, color:'var(--text-mid)', marginBottom:12 }}>
          This takes the order off the workboard and out of the sales figures.
          It stays in the order list so there's a record of it.
        </p>

        {pay.settled && (
          <div style={{
            background:'var(--red-pale)', border:'1px solid #f3c2c2', color:'var(--red)',
            borderRadius:'var(--radius-sm)', padding:'10px 12px', fontSize:13, marginBottom:12,
          }}>
            <strong>This order has been paid — {fmt(order.total)}.</strong> Cancelling
            it does not return the money; the refund has to be given separately.
          </div>
        )}

        <div className="form-group">
          <label className="field-label">Reason</label>
          <select className="input" value={reason} onChange={e=>setReason(e.target.value)} autoFocus>
            {CANCEL_REASONS.map(r => <option key={r}>{r}</option>)}
          </select>
        </div>
        {reason === 'Other' && (
          <div className="form-group">
            <label className="field-label">Say what happened</label>
            <input className="input" value={other} onChange={e=>setOther(e.target.value)}
              placeholder="Short note for the record"/>
          </div>
        )}

        <label className="checkbox-label">
          <input type="checkbox" checked={refund} onChange={e=>setRefund(e.target.checked)}/>
          {' '}Money needs refunding to the customer ({fmt(order.total)})
        </label>

        <div style={{display:'flex',gap:8,justifyContent:'flex-end',marginTop:16}}>
          <button className="btn btn-secondary" onClick={onClose}>Keep order</button>
          <button className="btn btn-danger" disabled={saving || (reason === 'Other' && !other.trim())}
            onClick={async () => { setSaving(true); await onConfirm({ reason: finalReason, refundDue: refund }); }}>
            {saving ? 'Cancelling…' : 'Cancel order'}
          </button>
        </div>
      </div>
    </div>
  );
}

/** Shown in place of the cancel button once an order has been cancelled. */
function CancelledPanel({ order, onRefunded }) {
  const c = cancellationOf(order);
  const [busy, setBusy] = useState(false);
  return (
    <div style={{
      background:'var(--red-pale)', border:'1px solid #f3c2c2',
      borderRadius:'var(--radius-sm)', padding:'10px 12px', marginBottom:8, fontSize:12.5,
    }}>
      <div style={{ fontWeight:800, color:'var(--red)' }}>CANCELLED</div>
      {c.reason && <div style={{ color:'var(--text-mid)' }}>{c.reason}</div>}
      {c.by && <div style={{ color:'var(--text-muted)' }}>by {c.by}</div>}

      {c.refundDue && (
        <>
          <div style={{ fontWeight:800, color:'var(--red)', marginTop:6 }}>
            REFUND DUE {fmt(c.refundAmount)}
          </div>
          <button className="btn btn-secondary btn-sm" style={{ width:'100%', marginTop:6 }}
            disabled={busy}
            onClick={async () => { setBusy(true); await onRefunded(); setBusy(false); }}>
            {busy ? 'Saving…' : 'Mark refunded'}
          </button>
        </>
      )}
      {c.refunded && (
        <div style={{ color:'var(--g700)', fontWeight:700, marginTop:6 }}>
          ✓ Refunded {fmt(c.refundAmount)}
        </div>
      )}
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    pending:   'pill-gold',  completed: 'pill-green',
    paid:      'pill-green', draft:     'pill-gray',
    void:      'pill-red',   ready:     'pill-blue',
  };
  return <span className={`pill ${map[status]||'pill-gray'}`}>{STATUS_LABEL[status] || capitalize(status||'')}</span>;
}

function fmtDate(ts) {
  if (!ts) return '—';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleDateString('en-CA', { month:'short', day:'numeric', year:'numeric' });
}
function capitalize(s) { return s ? s.charAt(0).toUpperCase() + s.slice(1) : ''; }
